import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { fetchInvoices } from "../../core/redux/slices/invoiceSlice";
import { fetchBanks } from "../../core/redux/slices/bankSlice";
import { getPaymentsInRange } from "../../core/api/invoiceService";
import { PAYMENT_MODES as BASE_PAYMENT_MODES } from "../../core/constants/paymentModes";
import { exportToCsv } from "../../core/utils/exportCsv";
import { getApiErrorMessage } from "../../core/utils/apiError";

const PAYMENT_MODES = [{ value: "", label: "All Modes" }, ...BASE_PAYMENT_MODES];

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const PaymentReport = () => {
  const dispatch = useDispatch();
  const invoices = useSelector((s) => s.invoices.list);
  const banks = useSelector((s) => s.bank.list);

  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [modeFilter, setModeFilter] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { dispatch(fetchInvoices()); dispatch(fetchBanks()); }, [dispatch]);

  const bankName = (id) => banks.find((b) => (b.bankID || b.id) === id)?.bankName || "—";

  const runReport = async () => {
    setLoading(true);
    try {
      const out = await getPaymentsInRange(invoices, dateFrom, dateTo);
      setRows(out);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to build the receipt report."), "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = (rows || []).filter((r) => !modeFilter || r.paymentMode === modeFilter);
  const total = filtered.reduce((s, r) => s + r.amount, 0);
  const byMode = filtered.reduce((acc, r) => { acc[r.paymentMode] = (acc[r.paymentMode] || 0) + r.amount; return acc; }, {});

  const handleExport = () => {
    const headers = ["Date", "Invoice #", "Patient", "Mode", "Bank", "Reference", "Amount"];
    const dataRows = filtered.map((r) => [
      r.paymentDate ? r.paymentDate.split("T")[0] : "", r.invoiceNumber, r.patientName, r.paymentMode,
      r.bankId ? bankName(r.bankId) : "", r.referenceNo, r.amount,
    ]);
    dataRows.push(["", "", "", "", "", "TOTAL", total]);
    exportToCsv(`Receipt-Report-${dateFrom}_to_${dateTo}.csv`, headers, dataRows);
  };

  const columns = [
    { title: "Date", dataIndex: "paymentDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "Invoice #", dataIndex: "invoiceNumber", width: 120 },
    { title: "Patient", dataIndex: "patientName" },
    { title: "Mode", dataIndex: "paymentMode", width: 120 },
    { title: "Bank", dataIndex: "bankId", width: 130, render: (v) => (v ? bankName(v) : "—") },
    { title: "Reference", dataIndex: "referenceNo" },
    { title: "Amount", dataIndex: "amount", align: "right", width: 130, render: fmt },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Receipt Report</h4><h6>All payments collected against patient invoices</h6></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="col-auto" style={{ minWidth: 160 }}>
                <label className="form-label small mb-1">Payment Mode</label>
                <Select classNamePrefix="react-select" options={PAYMENT_MODES}
                  value={PAYMENT_MODES.find((o) => o.value === modeFilter)}
                  onChange={(opt) => setModeFilter(opt?.value || "")} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={runReport} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-success" onClick={handleExport} disabled={filtered.length === 0}>
                  Export to Excel
                </button>
              </div>
            </div>
          </div>
        </div>

        {rows && (
          <>
            <div className="row mb-3">
              <div className="col-6 col-md-3 mb-2">
                <div className="card border-success h-100">
                  <div className="card-body text-center py-3">
                    <div className="text-success fw-bold" style={{ fontSize: "1.3rem" }}>{fmt(total)}</div>
                    <div className="text-muted small mt-1">Total Collected</div>
                  </div>
                </div>
              </div>
              {Object.entries(byMode).map(([mode, amt]) => (
                <div className="col-6 col-md-3 mb-2" key={mode}>
                  <div className="card h-100">
                    <div className="card-body text-center py-3">
                      <div className="fw-bold" style={{ fontSize: "1.3rem" }}>{fmt(amt)}</div>
                      <div className="text-muted small mt-1">{mode}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card table-list-card">
              <div className="card-body">
                <div className="table-responsive">
                  <Table columns={columns} dataSource={filtered} loading={loading} />
                </div>
              </div>
            </div>
          </>
        )}

        {!rows && !loading && (
          <div className="card text-center py-5 text-muted">
            <p>Select a date range and click <strong>Search</strong> to view collected payments.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReport;
